import React, { useCallback, useEffect, useState } from 'react';
import { Play } from 'lucide-react';
import api from '../lib/adminApi';
import config from '../config';
import { Button } from '../components/ui/button';
import { dateTime, humanize, message } from './api';
import { LoadState, Notice, Status } from './shared';
export const canaryEndpoint = `${config.apiBaseUrl}/admin-dashboard/canary`;
type Step = {
  name: string;
  status: 'pass' | 'fail' | 'skipped';
  durationMs: number;
  error?: string;
  detail?: string;
};
export type FlowResult = {
  flow: string;
  runId: string;
  trigger: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  status: 'pass' | 'fail';
  error: string | null;
  steps: Step[];
  facts: Record<string, unknown>;
  deployedVersion: string;
};
export type FlowState = {
  flow: string;
  state: 'pass' | 'fail' | 'stale' | 'no_data' | 'disabled';
  latest: FlowResult | null;
  history: FlowResult[];
};
export type RunSummary = {
  runId: string;
  trigger: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  status: 'pass' | 'fail';
  flows: Record<string, 'pass' | 'fail'>;
  skipped: Record<string, string>;
  swept: number;
  sweepError: string | null;
  deployedVersion: string;
};
type DeployRun = {
  id: number;
  status: string;
  conclusion: string | null;
  headSha: string;
  branch: string;
  event: string;
  createdAt: string;
  updatedAt: string;
  url: string;
};
export type ProductionStateData = {
  overall: 'healthy' | 'degraded' | 'unknown';
  running: boolean;
  checkedAt: string;
  deployed: {
    version: string;
    mainSha: string | null;
    mainCommittedAt: string | null;
    mainMessage: string | null;
    matchesMain: boolean | null;
    unavailableReason: string | null;
  };
  flows: FlowState[];
  runs: RunSummary[];
  deploys: { available: true; runs: DeployRun[] } | { available: false; reason: string };
  config: {
    scheduled: boolean;
    intervalMinutes: number;
    receiptMode: string;
    receiptIntervalHours: number;
    baseUrl: string;
  };
};
export const FLOW_TITLES: Record<string, string> = {
  health: 'API health and version',
  auth: 'Auth and session',
  manual_bill: 'Manual bill',
  share_link: 'Share link preview',
  receipt_upload: 'Receipt upload',
};
const STATE_TEXT: Record<FlowState['state'], string> = {
  pass: 'Passing',
  fail: 'Failing',
  stale: 'Stale',
  no_data: 'No results yet',
  disabled: 'Disabled',
};
const short = (sha?: string | null) => (sha ? sha.slice(0, 7) : 'unknown');
const seconds = (ms?: number) => (Number.isFinite(ms) ? `${((ms as number) / 1000).toFixed(1)}s` : '—');
const flowTitle = (flow: string) => FLOW_TITLES[flow] || humanize(flow);
function banner(data: ProductionStateData): [string, string] {
  const failing = data.flows.filter((f) => f.state === 'fail'),
    stale = data.flows.filter((f) => f.state === 'stale');
  if (data.overall === 'healthy')
    return [
      'Production is healthy.',
      data.deployed.matchesMain === false
        ? 'Every golden flow passed. The deployed version differs from main, so a deploy is pending.'
        : 'Every golden flow passed on its latest run.',
    ];
  if (data.overall === 'unknown')
    return ['No canary results yet.', 'Run the canaries now or enable the schedule to start collecting results.'];
  const parts = [
    failing.length ? `Failing: ${failing.map((f) => flowTitle(f.flow)).join(', ')}.` : '',
    stale.length ? `Stale: ${stale.map((f) => flowTitle(f.flow)).join(', ')}.` : '',
    data.deployed.matchesMain === false ? 'Deployed version differs from main.' : '',
  ].filter(Boolean);
  return ['Production is degraded.', parts.join(' ') || 'A golden flow has no recent passing run.'];
}
function versionText(deployed: ProductionStateData['deployed']) {
  if (deployed.matchesMain === true) return 'Matches main';
  if (deployed.matchesMain === false) return `Differs from main (${short(deployed.mainSha)})`;
  return `Main unavailable: ${deployed.unavailableReason || 'unknown reason'}`;
}
export default function ProductionState({ refresh, retry }: { refresh: number; retry: () => void }) {
  const [state, setState] = useState<{ data?: ProductionStateData; error?: string; loading: boolean }>({
    loading: true,
  });
  const [includeReceipt, setIncludeReceipt] = useState(false);
  const [starting, setStarting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [poll, setPoll] = useState(0);
  const load = useCallback(
    (signal: AbortSignal) =>
      api
        .get(`${canaryEndpoint}/state`, { signal })
        .then((response) => {
          if (!signal.aborted) setState({ data: response.data, loading: false });
        })
        .catch((error) => {
          if (!signal.aborted) setState((prev) => ({ data: prev.data, error: message(error), loading: false }));
        }),
    []
  );
  useEffect(() => {
    const controller = new AbortController();
    setState((prev) => ({ ...prev, error: undefined, loading: !prev.data }));
    load(controller.signal);
    return () => controller.abort();
  }, [load, refresh, poll]);
  const running = state.data?.running === true;
  // A run in progress stores each flow as it finishes; keep the view current.
  useEffect(() => {
    if (!running) return;
    const timer = window.setTimeout(() => setPoll((n) => n + 1), 5000);
    return () => window.clearTimeout(timer);
  }, [running, poll]);
  const start = async () => {
    setStarting(true);
    setNotice(null);
    try {
      const response = await api.post(`${canaryEndpoint}/run`, { includeReceipt });
      setNotice(
        response.data?.started
          ? 'Canary run started. Each flow appears below as it finishes.'
          : 'A canary run is already in progress.'
      );
      setPoll((n) => n + 1);
    } catch (error) {
      setNotice(message(error));
    } finally {
      setStarting(false);
    }
  };
  const data = state.data;
  const lastRun = data?.runs[0];
  return (
    <section className="admin-panel">
      <div className="admin-section-heading">
        <div>
          <h2>Is production doing its job?</h2>
          <p>Golden flows proven against production with synthetic, clearly marked data. Real accounts are never touched.</p>
        </div>
        <div className="admin-canary-actions">
          <label className="admin-check-label">
            <input
              type="checkbox"
              checked={includeReceipt}
              onChange={(e) => setIncludeReceipt(e.target.checked)}
            />
            Include receipt upload (one paid OCR call)
          </label>
          <Button onClick={start} disabled={starting || running}>
            <Play size={16} /> {running ? 'Running…' : 'Run canaries now'}
          </Button>
        </div>
      </div>
      <LoadState loading={state.loading} error={state.error} retry={retry} />
      {notice && <Notice>{notice}</Notice>}
      {data && (
        <>
          <div className={`admin-canary-banner ${data.overall}`} role="status">
            <strong>{banner(data)[0]}</strong>
            <span>{banner(data)[1]}</span>
          </div>
          <div className="admin-metrics admin-canary-metrics">
            <div className="admin-metric">
              <div className="admin-metric-heading">Deployed version</div>
              <strong className="admin-canary-sha" title={data.deployed.version}>
                {short(data.deployed.version)}
              </strong>
              <p>{versionText(data.deployed)}</p>
            </div>
            <div className="admin-metric">
              <div className="admin-metric-heading">Main</div>
              <strong className="admin-canary-sha" title={data.deployed.mainSha || undefined}>
                {short(data.deployed.mainSha)}
              </strong>
              <p>{data.deployed.mainMessage || (data.deployed.mainCommittedAt ? dateTime(data.deployed.mainCommittedAt) : 'Not available')}</p>
            </div>
            <div className="admin-metric">
              <div className="admin-metric-heading">Last run</div>
              <strong>{lastRun ? <Status value={lastRun.status} /> : 'None'}</strong>
              <p>{lastRun ? `${dateTime(lastRun.startedAt)} · ${humanize(lastRun.trigger)}` : 'No runs recorded'}</p>
            </div>
            <div className="admin-metric">
              <div className="admin-metric-heading">Schedule</div>
              <strong>{data.config.scheduled ? `Every ${data.config.intervalMinutes} min` : 'Off'}</strong>
              <p>
                Receipt canary{' '}
                {data.config.receiptMode === 'off' ? 'off' : `every ${data.config.receiptIntervalHours}h`}
              </p>
            </div>
          </div>
          <h3 className="admin-canary-subheading">Golden flows</h3>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Flow</th>
                  <th>State</th>
                  <th>Last run</th>
                  <th>Duration</th>
                  <th>Version</th>
                  <th>History</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.flows.map((flow) => {
                  const failedStep = flow.latest?.steps.find((step) => step.status === 'fail');
                  return (
                    <tr key={flow.flow}>
                      <td>{flowTitle(flow.flow)}</td>
                      <td>
                        <Status value={flow.state} /> <span className="sr-only">{STATE_TEXT[flow.state]}</span>
                      </td>
                      <td>{flow.latest ? dateTime(flow.latest.startedAt) : 'Never'}</td>
                      <td>{seconds(flow.latest?.durationMs)}</td>
                      <td className="admin-canary-sha">{short(flow.latest?.deployedVersion)}</td>
                      <td>
                        <span className="admin-canary-history" aria-label={`Last ${flow.history.length} results`}>
                          {flow.history
                            .slice()
                            .reverse()
                            .map((result) => (
                              <span
                                key={result.runId}
                                className={`admin-canary-dot ${result.status}`}
                                title={`${dateTime(result.startedAt)} · ${result.status}`}
                              />
                            ))}
                        </span>
                      </td>
                      <td>
                        {flow.state === 'stale' && flow.latest && (
                          <span className="admin-canary-note">No run since {dateTime(flow.latest.startedAt)}.</span>
                        )}
                        {flow.state === 'disabled' && <span className="admin-canary-note">CANARY_RECEIPT_MODE=off</span>}
                        {flow.latest?.status === 'fail' && (
                          <span className="admin-canary-detail">
                            {failedStep ? `${humanize(failedStep.name)}: ` : ''}
                            {flow.latest.error}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <h3 className="admin-canary-subheading">Recent runs</h3>
          {data.runs.length ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Trigger</th>
                    <th>Result</th>
                    <th>Flows</th>
                    <th>Duration</th>
                    <th>Version</th>
                  </tr>
                </thead>
                <tbody>
                  {data.runs.map((run) => (
                    <tr key={run.runId}>
                      <td>{dateTime(run.startedAt)}</td>
                      <td>{humanize(run.trigger)}</td>
                      <td>
                        <Status value={run.status} />
                      </td>
                      <td>
                        {Object.entries(run.flows)
                          .map(([flow, status]) => `${flowTitle(flow)} ${status}`)
                          .join(' · ') || 'None'}
                        {Object.keys(run.skipped || {}).length
                          ? ` · skipped ${Object.keys(run.skipped).map(flowTitle).join(', ')}`
                          : ''}
                        {run.swept ? ` · removed ${run.swept} leftover bill${run.swept === 1 ? '' : 's'}` : ''}
                      </td>
                      <td>{seconds(run.durationMs)}</td>
                      <td className="admin-canary-sha">{short(run.deployedVersion)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="admin-canary-note">No canary runs recorded yet.</p>
          )}
          <h3 className="admin-canary-subheading">Recent production deploys</h3>
          {data.deploys.available ? (
            data.deploys.runs.length ? (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Result</th>
                      <th>Commit</th>
                      <th>Branch</th>
                      <th>Run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.deploys.runs.map((deploy) => (
                      <tr key={deploy.id}>
                        <td>{dateTime(deploy.createdAt)}</td>
                        <td>
                          <Status value={deploy.conclusion || deploy.status} />
                        </td>
                        <td className="admin-canary-sha">{short(deploy.headSha)}</td>
                        <td>{deploy.branch}</td>
                        <td>
                          <a href={deploy.url} target="_blank" rel="noreferrer">
                            #{deploy.id}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="admin-canary-note">No production deploy runs found.</p>
            )
          ) : (
            <Notice>Deploy history is unavailable: {data.deploys.reason}</Notice>
          )}
          <p className="admin-canary-note">
            Checked {dateTime(data.checkedAt)} against {data.config.baseUrl}.
          </p>
        </>
      )}
    </section>
  );
}
